import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Chip, IconButton, Snackbar, Tooltip } from '@mui/material';
import BackArrowIcon from 'assets/images/iPrise/BackArrow.svg';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePatchItem } from 'services/common/react-query-common-service';
import { timeSince } from 'utils/ArrayConstants';
import { MASTER_ENDPOINT } from 'utils/Constant';

import { JobAssignStatus, JobQueueTableType } from '../../enum/TeamUserRoles';
import JobHeaderActions from './JobHeaderActions';
import JobSourcingSettingsModal from './JobSourcingSettingsModal';

interface JobHeaderProps {
  jobInfo: any;
  userDetail: any;
  refetch: any;
  tableType: JobQueueTableType;
}

const JobHeader: React.FC<JobHeaderProps> = ({
  tableType,
  jobInfo,
  userDetail,
  refetch,
}) => {
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState('');
  const [snackbarMsg, setSnackbarMsg] = React.useState('');
  const navigate = useNavigate();
  const sourceName = React.useMemo(() => {
    const src = jobInfo?.jobSource;
    if (!src) return '';
    return typeof src === 'string' ? src : src.name || '';
  }, [jobInfo]);
  const sourceLink = jobInfo?.jobSourceUrl;

  const { mutateAsync: patchItem, isPending: isAccepting } = usePatchItem();

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await patchItem({
        data: { sourceQueue: 'jobQueue' },
        endpoint: MASTER_ENDPOINT.AcceptJob,
        id: jobInfo?.id,
      });
      refetch();
    } catch (error) {
      console.error('Error accepting job:', error);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMsg(`${label} copied!`);
    } catch {
      setSnackbarMsg('Copy failed');
    }
  };

  // Memoized values for the job details
  const locationText = React.useMemo(() => {
    const city = jobInfo?.city?.trim();
    const state = jobInfo?.state?.trim();
    return city && state
      ? `${String(city)}, ${String(state)}`
      : String(city || state || '');
  }, [jobInfo]);

  const billRateText = React.useMemo(() => {
    const from = Number(jobInfo?.payRangeFrom);
    const to = Number(jobInfo?.payRangeTo);
    if (from > 0 && to > 0) return `$${from} - $${to}`;
    if (from > 0 || to > 0) return `$${from || to}`;
    return '-';
  }, [jobInfo]);

  const matchStatus = React.useMemo(() => {
    if (jobInfo?.candidateMatchStatus === 'submitted') {
      return { className: 'matches-submitted', label: 'Submitted' };
    }
    if (Number(jobInfo?.totalMatchedCandidates) > 0) {
      return { className: 'matches-found', label: 'Matches Found' };
    }
    return { className: 'no-matches', label: 'No Matches' };
  }, [jobInfo]);

  // Timer logic
  React.useEffect(() => {
    const isTLNew = tableType === JobQueueTableType.TEAM_LEAD_NEW;
    const isRecruiterNew = tableType === JobQueueTableType.RECRUITER_NEW;
    const isTLAssigned =
      jobInfo?.jobAssignStatus === JobAssignStatus.TL_ASSIGNED;
    const isRecruiterAssigned =
      jobInfo?.jobAssignStatus === JobAssignStatus.RECRUITER_ASSIGNED;

    const shouldSkipTimer =
      (!(isTLNew && isTLAssigned) &&
        !(isRecruiterNew && isRecruiterAssigned)) ||
      (isTLNew && !!jobInfo?.hasTlReachedUnallocatedQueue) ||
      (isRecruiterNew && !!jobInfo?.hasRecruiterReachedUnallocatedQueue) ||
      !jobInfo?.lastAssignedAt;

    if (shouldSkipTimer) {
      setTimeLeft('');
      return;
    }

    const calculate = () => {
      const remaining =
        new Date(jobInfo.lastAssignedAt).getTime() + 5 * 60 * 1000 - Date.now();
      if (remaining <= 0) {
        setTimeLeft('Expired');
        // navigate(-1);
        return;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setTimeLeft(
        minutes > 0
          ? `${minutes} min${minutes > 1 ? 's' : ''} ${seconds} sec left`
          : `${seconds} sec left`,
      );
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [jobInfo, tableType, navigate]);

  return (
    <Box className="job-header">
      <Box className="top-row">
        <Box className="title-area">
          <IconButton className="back-button" onClick={() => navigate(-1)}>
            <img src={BackArrowIcon} alt="Back" />
          </IconButton>
          <Box className="title-text">
            <h1>{jobInfo?.jobTitle || ''}</h1>
            <p className="subtitle">
              {jobInfo?.customer?.name || ''}
              <span className="subtitle-opacity">
                • {locationText}{' '}
                {jobInfo?.workType && `(${String(jobInfo.workType)})`} •{' '}
                {timeSince(jobInfo?.createdAt)} ago{' '}
              </span>
            </p>
          </Box>
        </Box>

        <JobHeaderActions
          jobInfo={jobInfo}
          userDetail={userDetail}
          refetch={refetch}
          tableType={tableType}
          timeLeft={timeLeft}
          isAccepting={isAccepting}
          handleAccept={handleAccept}
          setSettingsOpen={setSettingsOpen}
        />
      </Box>

      <JobSourcingSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        jobData={jobInfo}
        refetch={refetch}
      />

      <Box className="tag-row">
        <Box className={`tag ${matchStatus.className}`}>
          {matchStatus.label}
        </Box>
        <Box className="tag bill-rate">Bill Rate • {billRateText}</Box>
        {sourceName && sourceName.toLowerCase() === 'beeline' ? (
          <Tooltip
            title={
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <span>{jobInfo?.beelineJobPostingNumber || '-'}</span>
                {jobInfo?.beelineJobPostingNumber && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      void copyToClipboard(
                        jobInfo.beelineJobPostingNumber,
                        'ID',
                      );
                    }}
                    sx={{ color: '#fff', padding: '2px' }}>
                    <ContentCopyIcon sx={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </Box>
            }
            arrow>
            <Chip label={sourceName.toUpperCase()} className="source-chip" />
          </Tooltip>
        ) : sourceName &&
          ['email', 'manual'].includes(sourceName.toLowerCase()) ? (
          <Chip label={sourceName.toUpperCase()} className="source-chip" />
        ) : sourceLink ? (
          <Tooltip title={sourceLink} arrow>
            <Chip
              label={sourceName.toUpperCase() || 'LINK'}
              component="a"
              href={sourceLink}
              target="_blank"
              clickable
              className="source-chip source-chip-link"
            />
          </Tooltip>
        ) : sourceName ? (
          <Chip label={sourceName.toUpperCase()} className="source-chip" />
        ) : null}
      </Box>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={2000}
        onClose={() => setSnackbarMsg('')}
        message={snackbarMsg}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      />
    </Box>
  );
};

export default JobHeader;
